import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  formatPrice,
} from 'common/formatters';

import {
  INVOICE_STATUS_LIST,
} from 'common/financeConstants';

import {
  SR_API_ACTION_FINANCES_UPDATE_INVOICE_MERCHANT,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_INVOICES,
} from 'common/dataTableConstants';

import {
  dataTable,
  actionDataTableLoadItems,
  actionDataTableLoadNextPage,
  actionDataTableReset,
} from 'client/helpers/dataTables';

import log from 'client/lib/log';
import postWithState from 'client/actions/postWithState';
import SrTable from 'client/components/SrTable';
import SrLink from 'client/components/SrLink/SrLink';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';

function getDataTableSettingsFromProps(props) {
  const dataTableSettings = {};

  if (props.invoiceStatus) {
    dataTableSettings.filter = {
      'invoices.status': props.invoiceStatus,
    };
  }
  return dataTableSettings;
}

@dataTable((props) => ({
  dataSource: DATA_TABLE_SOURCE_INVOICES,
  viewId: props.viewId,
  sort: {
    'invoices.createdDate': 'desc',
  },
  ...getDataTableSettingsFromProps(props),
}))
@withInfiniteScroll({
  offset: 32,
})
class SrDataTableInvoices extends Component {
  static propTypes = {
    items: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    invoiceStatus: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    viewId: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
    dataTableApiState: PropTypes.object.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
  }

  constructor(props) {
    super(props);
    const { onInfiniteScrollCallbackReady } = props;
    this._isUnmounting = false;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  componentWillUnmount() {
    this._isUnmounting = true;
  }

  _infiniteScrollCallback = () => {
    const { viewId, dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadNextPage, {
        dataSource: DATA_TABLE_SOURCE_INVOICES,
        viewId: viewId,
        count: 8,
      });
    }
  }

  _onRefreshButtonClick = () => {
    const { dispatch } = this.props;
    dispatch(actionDataTableReset, {
      dataSource: DATA_TABLE_SOURCE_INVOICES,
    });
  }

  _onReloadItemRequested = (itemId) => {
    const { viewId, dispatch } = this.props;
    dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_INVOICES,
      viewId: viewId,
      filter: {
        'invoices.id': itemId,
      },
      count: 1,
    });
  }

  _onUpdateInvoiceMerchantClick = (event) => {
    const { dispatch } = this.props;
    const invoiceId = parseInt(event.target.dataset.invoiceId, 10);
    const merchantId = parseInt(global.prompt('Please enter the merchant ID.'), 10);
    if (!merchantId || isNaN(merchantId) || !invoiceId || isNaN(invoiceId)) {
      log.warning('Invalid invoiceId or merchantId', {
        invoiceId,
        merchantId,
      });
      return;
    }
    dispatch(postWithState, {
      action: SR_API_ACTION_FINANCES_UPDATE_INVOICE_MERCHANT,
      payload: {
        invoiceId,
        merchantId,
      },
      onError(error) {
        log.error(error);
        global.alert(error.message);
      },
    }).then(() => {
      if (!this._isUnmounting) {
        this._onReloadItemRequested(invoiceId);
      }
    });
  }

  render() {
    const {
      items,
      dataTableApiState,
      onInfiniteScrollWrapperRef,
    } = this.props;

    return (
      <div ref={onInfiniteScrollWrapperRef}>
        <div className='tableFiltersContainer'>
          <button
            className='floatButton refresh'
            type='button'
            onClick={this._onRefreshButtonClick}
          />
          {dataTableApiState.error && (
            <span>ERROR: { dataTableApiState.error }</span>
          )}
          <SrLink to='/admin/finances/invoices/all'>
            All
          </SrLink>
          <span className='sectionLabel'>By Status:</span>
          {INVOICE_STATUS_LIST.map((invoiceStatus) => (
            <SrLink
              key={invoiceStatus}
              to={`/admin/finances/invoices/by-status-${invoiceStatus}`}
            >
              {invoiceStatus}
            </SrLink>
          ))}
        </div>
        <SrTable
          headers={[
            { content: 'sunroom id' },
            { content: 'quickbooks id' },
            { content: 'merchant id' },
            { content: 'created' },
            { content: 'commission amount' },
            { content: 'balance' },
            { content: 'status' },
            { content: 'email recipient' },
            { content: 'due date' },
            { content: 'notes' },
            { content: 'locator' },
            { content: 'renter name' },
            { content: 'renter phone' },
            { content: 'apartment name' },
            { content: 'address' },
            { content: 'type' },
            { content: 'rent price' },
          ]}
          rows={items.map((invoice) => ({
            cells: [
              { content: invoice.id },
              { content: invoice.quickbooksInvoiceId || '--' },
              { content: invoice.merchantId || (
                <div
                  data-invoice-id={invoice.id}
                  onClick={this._onUpdateInvoiceMerchantClick}
                >
                  Add Merchant
                </div>
              ) },
              { content: moment(invoice.createdDate).format('MMM Do, YYYY - LTS') },
              { content: formatPrice(invoice.amount) },
              { content: formatPrice(invoice.balance) },
              { content: invoice.status },
              { content: invoice.emailRecipient || '--' },
              { content: moment(invoice.dueDate).format('MMM Do, YYYY') },
              { content: invoice.leaseNotes || '--' },
              { content: invoice.locatorName || '--' },
              { content: invoice.renterName },
              { content: invoice.renterPhone },
              { content: invoice.apartmentName || '--' },
              { content: invoice.addressStreetDisplay },
              { content: invoice.leaseType },
              { content: invoice.rentPrice },
            ],
          }))}
        />
        {dataTableApiState.inProgress && (
          <div className='loading-ellipses left' />
        )}
      </div>
    );
  }
}

SrDataTableInvoices.displayName = 'SrDataTableInvoices';

export default SrDataTableInvoices;
